import { __decorate } from "tslib";
import Map from 'core-js/library/fn/map';
import React, { cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';
import { action, computed, isArrayLike, observable, runInAction, toJS } from 'mobx';
import classNames from 'classnames';
import isPromise from 'is-promise';
import omit from 'lodash/omit';
import isNumber from 'lodash/isNumber';
import isString from 'lodash/isString';
import isNil from 'lodash/isNil';
import isLdEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';
import defaultTo from 'lodash/defaultTo';
import uniqWith from 'lodash/uniqWith';
import { isMoment } from 'moment';
import { observer } from 'mobx-react';
import noop from 'lodash/noop';
import isPlainObject from 'lodash/isPlainObject';
import KeyCode from '../../../es/_util/KeyCode';
import warning from '../../../es/_util/warning';
import { getConfig, getProPrefixCls } from '../../../es/configure';
import Row from '../../../es/row';
import Col from '../../../es/col';
import autobind from '../_util/autobind';
import { findBindFields } from '../data-set/utils';
import Validator from '../validator/Validator';
import FormContext from '../form/FormContext';
import DataSetComponent from '../data-set/DataSetComponent';
import Icon from '../icon';
import Tooltip from '../tooltip';
import isEmpty from '../_util/isEmpty';
import * as ObjectChainValue from '../_util/ObjectChainValue';
import { FieldTrim, FieldType, RecordStatus } from '../data-set/enum';
import { ShowHelp } from './enum';
import { FIELD_SUFFIX } from '../form/utils';
import { LabelLayout } from '../form/enum';
import Animate from '../animate';
import CloseButton from './CloseButton';
import { fromRangeValue, getDateFormatByField, toMultipleValue, toRangeValue } from './utils';
import isSame from '../_util/isSame';
import formatString from '../formatter/formatString';
import formatCurrency from '../formatter/formatCurrency';
const map = {};
export function getFieldsById(id) {
    if (!map[id]) {
        map[id] = [];
    }
    return map[id];
}
export class FormField extends DataSetComponent {
    constructor() {
        super(...arguments);
        this.emptyValue = null;
        this.lock = false;
        // 多选中出现了校验值的数量大于一那么输入框不需要存在校验信息展示
        this.mutipleValidateMessageLength = 0;
    }
    get validator() {
        const { field } = this;
        if (field) {
            if (!field.get('defaultValidationMessages')) {
                field.validator.props.defaultValidationMessages = this.getValidatorProps().defaultValidationMessages;
            }
            return field.validator;
        }
        return new Validator(undefined, this);
    }
    get name() {
        return this.observableProps.name;
    }
    get value() {
        return this.observableProps.value;
    }
    set value(value) {
        runInAction(() => {
            this.observableProps.value = value;
        });
    }
    get labelLayout() {
        return this.props.labelLayout || this.context.labelLayout;
    }
    get hasFloatLabel() {
        const { labelLayout } = this;
        return labelLayout === LabelLayout.float;
    }
    get isControlled() {
        return this.props.value !== undefined;
    }
    get pristine() {
        return this.props.pristine || this.context.pristine;
    }
    get defaultValidationMessages() {
        return {};
    }
    // @computed
    get editable() {
        return !this.isDisabled() && !this.isReadOnly();
    }
    get dataSet() {
        const { record } = this;
        if (record) {
            return record.dataSet;
        }
        return this.observableProps.dataSet;
    }
    get record() {
        const { record, dataSet, dataIndex } = this.observableProps;
        if (record) {
            return record;
        }
        if (dataSet) {
            if (isNumber(dataIndex)) {
                return dataSet.get(dataIndex);
            }
            return dataSet.current;
        }
        return undefined;
    }
    get field() {
        const { record, dataSet, name, observableProps } = this;
        const { displayName } = this.constructor;
        if (displayName !== 'Output' && !name) {
            warning(!observableProps.dataSet, `${displayName} with binding DataSet need property name.`);
            warning(!observableProps.record, `${displayName} with binding Record need property name.`);
        }
        if (name) {
            const recordField = record ? record.getField(name) : undefined;
            const dsField = dataSet ? dataSet.getField(name) : undefined;
            if (recordField) {
                return recordField;
            }
            return dsField;
        }
        return undefined;
    }
    get isValid() {
        return this.pristine || (this.field ? this.field.valid : this.validator.validity.valid);
    }
    get multiple() {
        return this.getProp('multiple');
    }
    /**
     * 获取字段多行属性
     */
    get multiLine() {
        return this.getProp('multiLine');
    }
    /**
     * 获取字段货币属性
     */
    get currency() {
        return this.getProp('currency');
    }
    get trim() {
        return this.getProp('trim');
    }
    get format() {
        return this.getProp('format');
    }
    get range() {
        return this.getProp('range');
    }
    defaultRenderer({ text, repeat, maxTagTextLength }) {
        return repeat !== undefined &&
            maxTagTextLength &&
            isString(text) &&
            text.length > maxTagTextLength
            ? `${text.slice(0, maxTagTextLength)}...`
            : text;
    }
    /**
     * 处理多行关联字段校验
     * @param field
     */
    multiLineValidator(field) {
        if (field) {
            if (!field.get('defaultValidationMessages')) {
                field.validator.props.defaultValidationMessages = this.getValidatorProps().defaultValidationMessages;
            }
            return field.validator;
        }
        return new Validator(undefined, this);
    }
    /**
     * 判断是否应该显示验证信息, 作为属性传给Tooltip
     *
     * @readonly
     * @type {(undefined | boolean)}
     * @memberof FormField
     */
    isValidationMessageHidden(message) {
        const { hidden, noValidate } = this.props;
        if (hidden || this.pristine || (!this.record && noValidate) || !message) {
            return true;
        }
    }
    isEmpty() {
        const value = this.getValue();
        return isArrayLike(value) ? !value.length : isEmpty(value);
    }
    getObservableProps(props, context) {
        return {
            ...super.getObservableProps(props, context),
            name: props.name,
            record: 'record' in props ? props.record : context.record,
            dataSet: 'dataSet' in props ? props.dataSet : context.dataSet,
            dataIndex: defaultTo(props.dataIndex, context.dataIndex),
            value: 'value' in props ? props.value : this.observableProps ? this.observableProps.value : props.defaultValue,
        };
    }
    getOtherProps() {
        const otherProps = omit(super.getOtherProps(), [
            'record',
            'defaultValue',
            'dataIndex',
            'onEnterDown',
            'onClear',
            'onBeforeChange',
            'readOnly',
            'validator',
            'validationRenderer',
            'help',
            'showHelp',
            'renderer',
            'maxTagPlaceholder',
            'maxTagCount',
            'maxTagTextLength',
            'rowIndex',
            'colIndex',
            'labelLayout',
            '_inTable',
            'labelWidth',
            'pristine',
            'range',
            'trim',
            'newLine',
            'fieldClassName',
            'preventRenderer',
        ]);
        otherProps.onChange = !this.isDisabled() && !this.isReadOnly() ? this.handleChange : noop;
        otherProps.onKeyDown = this.handleKeyDown;
        otherProps.onCompositionStart = this.handleCompositionStart;
        otherProps.onCompositionEnd = this.handleChange;
        return otherProps;
    }
    getWrapperClassNames(...args) {
        const { prefixCls } = this;
        return super.getWrapperClassNames({
            [`${prefixCls}-invalid`]: !this.isValid,
            [`${prefixCls}-float-label`]: this.hasFloatLabel,
            [`${prefixCls}-required`]: this.getProp('required'),
            [`${prefixCls}-readonly`]: this.getProp('readOnly'),
        }, ...args);
    }
    renderWrapper() {
        return undefined;
    }
    renderHelpMessage() {
        const { showHelp } = this.props;
        const help = this.getProp('help');
        if (showHelp === ShowHelp.newLine && help) {
            return (React.createElement("div", { key: "help", className: `${getProPrefixCls(FIELD_SUFFIX)}-help` }, help));
        }
    }
    getLabel() {
        return this.getProp('label');
    }
    renderFloatLabel() {
        if (this.hasFloatLabel && (this.isFocus || !this.range || !this.isEmpty())) {
            const label = this.getLabel();
            if (label) {
                const prefixCls = getProPrefixCls(FIELD_SUFFIX);
                const required = this.getProp('required');
                const readOnly = this.getProp('readOnly');
                const classString = classNames(`${prefixCls}-label`, {
                    [`${prefixCls}-required`]: required,
                    [`${prefixCls}-readonly`]: readOnly,
                });
                return (React.createElement("div", { className: `${prefixCls}-label-wrapper` },
                    React.createElement("div", { className: classString }, label)));
            }
        }
    }
    componentDidMount() {
        this.addToForm(this.props, this.context);
    }
    componentWillReceiveProps(nextProps, nextContext) {
        super.componentWillReceiveProps(nextProps, nextContext);
        this.removeFromForm(this.props, this.context);
        this.addToForm(nextProps, nextContext);
        if (!this.record && this.props.value !== nextProps.value) {
            this.validate(nextProps.value);
        }
    }
    componentWillUnmount() {
        this.removeFromForm(this.props, this.context);
    }
    addToForm(props, context) {
        const { form } = props;
        const { formNode } = context;
        if (form) {
            let fields = map[form];
            if (!fields) {
                fields = [];
                map[form] = fields;
            }
            fields.push(this);
        }
        else if (formNode) {
            formNode.addField(this);
        }
    }
    removeFromForm(props, context) {
        const { form } = props;
        const { formNode } = context;
        if (form) {
            const fields = map[form];
            if (fields) {
                const index = fields.indexOf(this);
                if (index !== -1) {
                    fields.splice(index, 1);
                }
            }
        }
        else if (formNode) {
            formNode.removeField(this);
        }
    }
    renderValidationMessage(validationResult) {
        const validationMessage = this.getValidationMessage(validationResult);
        if (validationMessage) {
            return (React.createElement("div", { className: getProPrefixCls('validation-message') },
                this.context.labelLayout !== LabelLayout.float && React.createElement(Icon, { type: "error" }),
                React.createElement("span", null, validationMessage)));
        }
    }
    getValidatorProps() {
        const { name, range, multiple, defaultValidationMessages } = this;
        const type = this.getFieldType();
        const required = this.getProp('required');
        const customValidator = this.getProp('validator');
        const label = this.getProp('label');
        return {
            type,
            required,
            customValidator,
            name,
            label,
            range,
            multiple,
            defaultValidationMessages,
            form: this.context.formNode,
        };
    }
    getValidationMessage(validationResult = this.validator.currentValidationResult) {
        const { validator, props: { validationRenderer }, } = this;
        if (validationResult) {
            if (validationRenderer) {
                const validationMessage = validationRenderer(validationResult, validator.props);
                if (validationMessage) {
                    return validationMessage;
                }
            }
            return validationResult.validationMessage;
        }
    }
    handleFocus(e) {
        super.handleFocus(e);
        if (this.range) {
            this.beginRange();
        }
    }
    handleBlur(e) {
        super.handleBlur(e);
        if (this.range) {
            this.endRange();
        }
    }
    handleCompositionStart() {
        this.lock = true;
    }
    handleChange(e) {
        this.lock = false;
        e.preventDefault();
        e.stopPropagation();
    }
    handleKeyDown(e) {
        const { onKeyDown = noop, onEnterDown = noop } = this.props;
        onKeyDown(e);
        if (!e.isDefaultPrevented()) {
            switch (e.keyCode) {
                case KeyCode.ENTER:
                    this.handleEnterDown(e);
                    onEnterDown(e);
                    break;
                case KeyCode.ESC:
                    this.blur();
                    break;
                default:
            }
        }
    }
    handleEnterDown(e) {
        if (this.multiple) {
            if (this.range) {
                this.endRange();
                e.preventDefault();
            }
            else {
                const { value } = e.target;
                if (value !== '') {
                    this.syncValueOnBlur(value);
                    e.preventDefault();
                }
            }
        }
        else {
            this.blur();
        }
    }
    syncValueOnBlur(value) {
        this.prepareSetValue(value);
    }
    handleMutipleValueRemove(e, value, index) {
        this.removeValue(value, index);
        e.stopPropagation();
    }
    getDateFormat(field = this.field) {
        return getDateFormatByField(field, this.getFieldType(field));
    }
    processValue(value) {
        if (!isNil(value)) {
            if (isMoment(value)) {
                return value.format(this.getDateFormat());
            }
            if (isValidElement(value)) {
                // For Select's Option and TreeSelect's TreeNode which type may be ReactElement
                // @ts-ignore
                return value;
            }
            return value.toString();
        }
        return '';
    }
    isReadOnly() {
        const { readOnly } = this.context;
        return (readOnly ||
            this.getProp('readOnly') ||
            this.pristine ||
            (this.isControlled && !this.props.onChange && !this.props.onInput));
    }
    getDataSetValue() {
        const { record, pristine, name } = this;
        if (record) {
            return pristine ? record.getPristineValue(name) : record.get(name);
        }
    }
    getTextNode() {
        const { preventRenderer } = this.props;
        const text = this.editable && (preventRenderer || this.isFocused)
            ? this.processValue(this.getValue())
            : this.processRenderer(this.getValue());
        return text;
    }
    getText(value) {
        return this.processValue(value);
    }
    processText(value) {
        return value;
    }
    processRenderer(value, repeat) {
        const { field, record, dataSet, props: { renderer = this.defaultRenderer, name, maxTagTextLength }, } = this;
        let processValue;
        if (field && (field.lookup || field.options)) {
            processValue = field.getText(value);
        }
        // 值集中不存在 再去取直接返回的值
        const text = this.processText(isNil(processValue) ? this.getText(value) : processValue);
        return renderer
            ? renderer({
                value,
                text,
                record,
                dataSet,
                name,
                repeat,
                maxTagTextLength,
            })
            : text;
    }
    processRangeValue(value, repeat) {
        if (repeat === undefined) {
            value = this.rangeValue;
        }
        if (value === undefined && !this.multiple) {
            value = toRangeValue(this.getValue(), this.range);
        }
        return (value || []).map(item => this.processRenderer(item, repeat));
    }
    getOldValue() {
        return this.getValue();
    }
    getValue() {
        const { name } = this;
        if (this.dataSet && name) {
            return this.getDataSetValue();
        }
        return this.value;
    }
    getValues() {
        return toMultipleValue(this.getValue(), this.range);
    }
    addValue(...values) {
        if (this.multiple) {
            const oldValues = this.getValues();
            if (values.length) {
                this.setValue(uniqWith([...oldValues, ...values], this.compare));
            }
            else if (!oldValues.length) {
                this.setValue(this.emptyValue);
            }
        }
        else {
            this.setValue(values.pop());
        }
    }
    isLowerRange(_value1, _value2) {
        return false;
    }
    prepareSetValue(...value) {
        const { range } = this;
        const values = value.filter(item => isNumber(item) || !isEmpty(item));
        if (range) {
            const { rangeTarget, rangeValue } = this;
            if (rangeTarget !== undefined && rangeValue) {
                const [start, end] = rangeValue;
                const newValue = values.pop();
                rangeValue[rangeTarget] = newValue;
                if (rangeTarget === 0 && (newValue || isNumber(newValue)) && (end || isNumber(end)) && this.isLowerRange(end, newValue)) {
                    rangeValue[rangeTarget] = end;
                    rangeValue[1] = newValue;
                }
                if (rangeTarget === 1 && (newValue || isNumber(newValue)) && (start || isNumber(start)) && this.isLowerRange(newValue, start)) {
                    rangeValue[rangeTarget] = start;
                    rangeValue[0] = newValue;
                }
            }
        }
        else {
            this.addValue(...values);
        }
    }
    removeValues(values, index = 0) {
        let repeat;
        this.setValue(values.reduce((oldValues, value) => {
            repeat = 0;
            return oldValues.filter(v => {
                if (this.getValueKey(v) === this.getValueKey(value)) {
                    if (index === -1 || repeat === index) {
                        this.afterRemoveValue(value, repeat++);
                        return false;
                    }
                    repeat++;
                }
                return true;
            });
        }, this.getValues()));
    }
    removeValue(value, index = 0) {
        this.removeValues([value], index);
    }
    afterRemoveValue(_value, _repeat) {
    }
    beginRange() {
        this.setRangeTarget(this.rangeTarget || 0);
        this.rangeValue = this.multiple
            ? [undefined, undefined]
            : toRangeValue(this.getValue(), this.range);
    }
    endRange() {
        if (this.rangeValue) {
            const values = this.rangeValue.slice();
            this.rangeValue = undefined;
            if (!this.multiple || !values.every(isNil)) {
                this.addValue(fromRangeValue(values, this.range));
            }
        }
    }
    setRangeTarget(target) {
        this.rangeTarget = target;
    }
    compare(oldValue, newValue) {
        return isSame(toJS(oldValue), toJS(newValue));
    }
    setValue(value) {
        if (!this.isReadOnly()) {
            if (this.multiple || this.range
                ? isArrayLike(value) && !value.length
                : isNil(value) || value === '') {
                value = this.emptyValue;
            }
            const { name, dataSet, trim, format, observableProps: { dataIndex }, } = this;
            const { onChange = noop, onBeforeChange = noop } = this.props;
            const old = toJS(this.getOldValue());
            if (!dataSet || !name) {
                value = formatString(value, {
                    trim,
                    format,
                });
            }
            const updateAndValidate = () => {
                if (dataSet && name) {
                    (this.record || dataSet.create({}, dataIndex)).set(name, value);
                }
                else {
                    this.validate(value);
                }
            };
            // 转成实际的数据再进行判断
            if (!this.compare(old, toJS(value))) {
                const { formNode } = this.context;
                const storedValue = this.value;
                const beforeChange = onBeforeChange(value, old, formNode);
                const resolveCallback = action(() => {
                    updateAndValidate();
                    onChange(value, old, formNode);
                    this.afterSetValue();
                });
                if (isPromise(beforeChange)) {
                    this.value = value;
                    const rejectCallback = () => {
                        this.value = storedValue;
                    };
                    beforeChange.then(result => result === false ? rejectCallback() : resolveCallback());
                }
                else if (beforeChange !== false) {
                    resolveCallback();
                    this.value = value;
                }
            }
            else {
                updateAndValidate();
            }
        }
    }
    afterSetValue() {
    }
    renderRangeValue(readOnly, value, repeat) {
        const rangeValue = this.processRangeValue(value, repeat);
        if (readOnly) {
            if (rangeValue.length) {
                return (React.createElement(React.Fragment, null,
                    rangeValue[0],
                    "~",
                    rangeValue[1]));
            }
        }
    }
    /**
     * 渲染货币格式化
     * @param readOnly
     */
    renderCurrency(readOnly) {
        const { currency, lang, props: { renderer }, } = this;
        if (renderer) {
            return this.getTextNode();
        }
        if (readOnly) {
            const formatOptions = {
                currency,
            };
            const value = this.getValue();
            return formatCurrency(value, lang, formatOptions);
        }
    }
    /**
     * 只读模式下多行单元格渲染
     * @param readOnly
     */
    renderMultiLine(readOnly) {
        const { name, record, field, dataSet, prefixCls, props: { renderer }, } = this;
        const multiLineFields = findBindFields(field, record.fields, true);
        if (renderer) {
            return renderer({
                multiLineFields,
                record,
                dataSet,
                name,
            });
        }
        if (readOnly) {
            if (multiLineFields.length) {
                this.mutipleValidateMessageLength = 0;
                return (React.createElement(React.Fragment, null, multiLineFields.map(fieldItem => {
                    if (fieldItem) {
                        const { validationResults } = this.multiLineValidator(fieldItem);
                        const required = defaultTo(fieldItem && fieldItem.get('required'), this.props.required);
                        const repeats = new Map();
                        const validationResult = validationResults.find(error => error.value === record?.get(fieldItem.get('name')));
                        const validationMessage = validationResult && this.renderValidationMessage(validationResult);
                        const key = this.getValueKey(record?.get(fieldItem.get('name')));
                        const repeat = repeats.get(key) || 0;
                        const validationHidden = this.isValidationMessageHidden(validationMessage);
                        let processValue = '';
                        if (fieldItem && fieldItem.get('lovCode')) {
                            if (!isNil(fieldItem.getValue())) {
                                if (isPlainObject(fieldItem.getValue())) {
                                    processValue = ObjectChainValue.get(fieldItem.getValue(), fieldItem.get('textField') || 'meaning');
                                }
                            }
                        }
                        // 值集中不存在 再去取直接返回的值
                        const text = this.processText(processValue || this.getText(record?.get(fieldItem.get('name'))));
                        this.mutipleValidateMessageLength++;
                        const inner = record?.status === RecordStatus.add ? '' :
                            React.createElement("span", { className: `${prefixCls}-multi-value-invalid` }, text);
                        const validationInner = validationHidden ? inner : (React.createElement(Tooltip, { suffixCls: `form-tooltip ${getConfig('proPrefixCls')}-tooltip`, key: `${key}-${repeat}`, title: validationMessage, theme: "light", placement: "bottomLeft", hidden: validationHidden }, validationMessage));
                        const label = fieldItem.get('label');
                        return (React.createElement(Row, { key: `${record?.index}-multi-${fieldItem.get('name')}`, className: `${prefixCls}-multi` },
                            label && (React.createElement(Col, { span: 8, className: required ? `${prefixCls}-multi-label ${prefixCls}-multi-label-required` : `${prefixCls}-multi-label` }, fieldItem.get('label'))),
                            React.createElement(Col, { span: label ? 16 : 24, className: validationHidden ?
                                    `${prefixCls}-multi-value` :
                                    `${prefixCls}-multi-value ${prefixCls}-multi-value-invalid` }, record?.get(fieldItem.get('name')) ?
                                (React.createElement(Tooltip, { suffixCls: `form-tooltip ${getConfig('proPrefixCls')}-tooltip`, key: `${key}-${repeat}`, title: validationMessage, theme: "light", placement: "bottomLeft", hidden: validationHidden }, text)) : validationInner)));
                    }
                    return null;
                })));
            }
        }
    }
    getValueKey(v) {
        if (isArrayLike(v)) {
            return v.join(',');
        }
        return v;
    }
    isMultipleBlockDisabled(_v) {
        return this.isDisabled();
    }
    renderMultipleValues(readOnly) {
        const values = this.getValues();
        const valueLength = values.length;
        const { prefixCls, range, props: { maxTagCount = valueLength, maxTagPlaceholder }, } = this;
        const { validationResults } = this.validator;
        const repeats = new Map();
        const blockClassName = classNames({
            [`${prefixCls}-multiple-block-disabled`]: this.isDisabled(),
        }, `${prefixCls}-multiple-block`);
        this.mutipleValidateMessageLength = 0;
        const tags = values.slice(0, maxTagCount).map((v, index) => {
            const key = this.getValueKey(v);
            const repeat = repeats.get(key) || 0;
            const text = range ? this.renderRangeValue(true, v, repeat) : this.processRenderer(v, repeat);
            repeats.set(key, repeat + 1);
            if (!isNil(text)) {
                const validationResult = validationResults.find(error => error.value === v);
                const disabled = this.isMultipleBlockDisabled(v);
                const className = classNames({
                    [`${prefixCls}-multiple-block-invalid`]: validationResult,
                    [`${prefixCls}-multiple-block-disabled`]: disabled,
                }, `${prefixCls}-multiple-block`);
                const validationMessage = validationResult && this.renderValidationMessage(validationResult);
                if (validationMessage) {
                    this.mutipleValidateMessageLength++;
                }
                const closeBtn = !disabled && !this.isReadOnly() && (React.createElement(CloseButton, { onClose: this.handleMutipleValueRemove, value: v, index: repeat }));
                const inner = readOnly ? (React.createElement("span", { className: className }, text)) : (React.createElement("li", { className: className },
                    React.createElement("div", null, text),
                    closeBtn));
                return (React.createElement(Tooltip, { suffixCls: `form-tooltip ${getConfig('proPrefixCls')}-tooltip`, key: String(index), title: validationMessage, theme: "light", placement: "bottomLeft", hidden: this.isValidationMessageHidden(validationMessage) }, inner));
            }
            return undefined;
        });
        if (valueLength > maxTagCount) {
            let content = `+ ${valueLength - maxTagCount} ...`;
            if (maxTagPlaceholder) {
                const omittedValues = values.slice(maxTagCount, valueLength);
                content =
                    typeof maxTagPlaceholder === 'function'
                        ? maxTagPlaceholder(omittedValues)
                        : maxTagPlaceholder;
            }
            tags.push(React.createElement("li", { key: "maxTagPlaceholder", className: blockClassName },
                React.createElement("div", null, content)));
        }
        return tags;
    }
    clear() {
        const { onClear = noop } = this.props;
        this.setValue(this.emptyValue);
        this.rangeValue = this.isFocused ? [undefined, undefined] : undefined;
        onClear();
    }
    async checkValidity() {
        const { name } = this;
        const valid = await this.validate();
        const { onInvalid = noop } = this.props;
        if (!valid) {
            const { validationResults, validity } = this.validator;
            onInvalid(validationResults, validity, name);
        }
        return valid;
    }
    async validate(value) {
        let invalid = false;
        if (!this.props.noValidate) {
            if (value === undefined) {
                value = this.multiple ? this.getValues() : this.getValue();
            }
            const { validator } = this;
            validator.reset();
            invalid = !(await validator.checkValidity(value));
        }
        return !invalid;
    }
    isDisabled() {
        const { disabled } = this.context;
        if (disabled || this.getProp('disabled')) {
            return true;
        }
        const { field, record } = this;
        if (field) {
            const cascadeMap = field.get('cascadeMap');
            if (cascadeMap &&
                (!record || Object.keys(cascadeMap).some(cascade => {
                    if (isObject(record.get(cascadeMap[cascade]))) {
                        return isLdEmpty(record.get(cascadeMap[cascade]));
                    }
                    return isNil(record.get(cascadeMap[cascade]));
                }))) {
                return true;
            }
        }
        return super.isDisabled();
    }
    reset() {
        if (!this.isControlled && !this.dataSet) {
            this.setValue(this.props.defaultValue);
        }
        this.validator.reset();
    }
    getFieldType(field = this.field) {
        return (field && field.get('type')) || FieldType.string;
    }
    getProp(propName) {
        const { field, observableProps } = this;
        return defaultTo(field && field.get(propName), propName in observableProps ? observableProps[propName] : this.props[propName]);
    }
    render() {
        const validationMessage = this.renderValidationMessage();
        const wrapper = this.renderWrapper();
        const help = this.renderHelpMessage();
        const { _inTable } = this.props;
        /**
         * 用户自定义校验存在的话说明用户保证校验情况那么多选这些应该存在校验信息
         * If the user-defined verification exists, it means that the user guarantees
         * that the verification situation is so many. These should have verification information
         */
        const customValidator = this.getProp('validator');
        return this.hasFloatLabel ? ([
            isValidElement(wrapper) && cloneElement(wrapper, { key: 'wrapper' }),
            React.createElement(Animate, { transitionName: "show-error", component: "", transitionAppear: true, key: "validation-message" }, validationMessage),
            help,
        ]) :
            _inTable ?
                React.createElement(React.Fragment, null,
                    wrapper,
                    help)
                : (React.createElement(Tooltip, { suffixCls: `form-tooltip ${getConfig('proPrefixCls')}-tooltip`, title: (!!(this.multiple && this.getValues().length) && !customValidator || this.mutipleValidateMessageLength > 0) ||
                        this.isValidationMessageHidden(validationMessage)
                        ? null
                        : validationMessage, theme: "light", placement: "bottomLeft" },
                    wrapper,
                    help));
    }
}
FormField.contextType = FormContext;
FormField.propTypes = {
    _inTable: PropTypes.bool,
    type: PropTypes.string,
    /**
     * 字段名
     */
    name: PropTypes.string,
    /**
     * <受控>当前值
     */
    value: PropTypes.any,
    /**
     * 默认值
     */
    defaultValue: PropTypes.any,
    /**
     * 是否必输
     */
    required: PropTypes.bool,
    /**
     * 是否只读
     */
    readOnly: PropTypes.bool,
    /**
     * 对照表单id
     */
    form: PropTypes.string,
    /**
     * 对照record在DataSet中的index
     * @default dataSet.currentIndex
     */
    dataIndex: PropTypes.number,
    /**
     * 是否是多值
     * @default false
     */
    multiple: PropTypes.bool,
    /**
     * 表单下控件跨越的行数
     */
    rowSpan: PropTypes.number,
    /**
     * 另起新行
     */
    newLine: PropTypes.bool,
    /**
     * 表单下控件跨越的列数
     */
    colSpan: PropTypes.number,
    /**
     * 校验器
     * (value: any, name?: string, form?: ReactInstance) => string | boolean | Promise<string | boolean>
     */
    validator: PropTypes.func,
    /**
     * 校验失败回调
     * (validationMessage: ReactNode, validity: Validity, name?: string) => void
     */
    onInvalid: PropTypes.func,
    /**
     * 额外信息，常用作提示
     */
    help: PropTypes.string,
    /**
     * 显示提示信息的方式
     */
    showHelp: PropTypes.oneOf([ShowHelp.tooltip, ShowHelp.newLine, ShowHelp.none]),
    /**
     * 渲染器
     */
    renderer: PropTypes.func,
    /**
     * 校验信息渲染器
     */
    validationRenderer: PropTypes.func,
    /**
     * 多值标签超出最大数量时的占位描述
     */
    maxTagPlaceholder: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    /**
     * 多值标签最大数量
     */
    maxTagCount: PropTypes.number,
    /**
     * 多值标签文案最大长度
     */
    maxTagTextLength: PropTypes.number,
    /**
     * 显示原始值
     */
    pristine: PropTypes.bool,
    /**
     * 字符串值是否去掉首尾空格
     * 可选值: both left right none
     * @default: both
     */
    trim: PropTypes.oneOf([FieldTrim.both, FieldTrim.left, FieldTrim.right, FieldTrim.none]),
    /**
     * 值变化前回调
     * (value: any, oldValue: any) => boolean
     */
    onBeforeChange: PropTypes.func,
    /**
     * 值变化回调
     * (value: any, oldValue: any, form?: ReactInstance) => void
     */
    onChange: PropTypes.func,
    /**
     * 输入回调
     */
    onInput: PropTypes.func,
    /**
     * 键盘回车回调
     */
    onEnterDown: PropTypes.func,
    /**
     * 键盘回车回调
     */
    fieldClassName: PropTypes.string,
    ...DataSetComponent.propTypes,
};
FormField.defaultProps = {
    readOnly: false,
    disabled: false,
    noValidate: false,
    showHelp: 'newLine',
    trim: FieldTrim.both,
};
__decorate([
    observable
], FormField.prototype, "rangeTarget", void 0);
__decorate([
    observable
], FormField.prototype, "rangeValue", void 0);
__decorate([
    computed
], FormField.prototype, "validator", null);
__decorate([
    computed
], FormField.prototype, "name", null);
__decorate([
    computed
], FormField.prototype, "value", null);
__decorate([
    computed
], FormField.prototype, "defaultValidationMessages", null);
__decorate([
    computed
], FormField.prototype, "dataSet", null);
__decorate([
    computed
], FormField.prototype, "record", null);
__decorate([
    computed
], FormField.prototype, "field", null);
__decorate([
    computed
], FormField.prototype, "isValid", null);
__decorate([
    computed
], FormField.prototype, "multiple", null);
__decorate([
    computed
], FormField.prototype, "multiLine", null);
__decorate([
    computed
], FormField.prototype, "currency", null);
__decorate([
    computed
], FormField.prototype, "trim", null);
__decorate([
    computed
], FormField.prototype, "format", null);
__decorate([
    computed
], FormField.prototype, "range", null);
__decorate([
    autobind
], FormField.prototype, "defaultRenderer", null);
__decorate([
    autobind
], FormField.prototype, "handleFocus", null);
__decorate([
    autobind
], FormField.prototype, "handleBlur", null);
__decorate([
    autobind
], FormField.prototype, "handleCompositionStart", null);
__decorate([
    autobind
], FormField.prototype, "handleChange", null);
__decorate([
    autobind
], FormField.prototype, "handleKeyDown", null);
__decorate([
    autobind
], FormField.prototype, "handleMutipleValueRemove", null);
__decorate([
    action
], FormField.prototype, "prepareSetValue", null);
__decorate([
    action
], FormField.prototype, "beginRange", null);
__decorate([
    action
], FormField.prototype, "endRange", null);
__decorate([
    action
], FormField.prototype, "setRangeTarget", null);
__decorate([
    action
], FormField.prototype, "setValue", null);
__decorate([
    action
], FormField.prototype, "clear", null);
__decorate([
    autobind
], FormField.prototype, "reset", null);
let ObserverFormField = class ObserverFormField extends FormField {
};
ObserverFormField.defaultProps = FormField.defaultProps;
ObserverFormField = __decorate([
    observer
], ObserverFormField);
export default ObserverFormField;
